
import { useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Search, Plus } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useSearchFoods } from '@/api/food-library'
import type { FoodItem } from '@/types/diet-v2'

interface FoodSearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectFood: (food: FoodItem) => void
  onCreateCustomFood?: () => void
}

export function FoodSearchDialog({ open, onOpenChange, onSelectFood, onCreateCustomFood }: FoodSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery] = useDebounce(searchQuery, 500)
  const [source, setSource] = useState<'usda' | 'custom' | undefined>(undefined)

  const { data: foods = [], isLoading } = useSearchFoods(debouncedQuery, source)

  const handleSelectFood = (food: FoodItem) => {
    onSelectFood(food)
    onOpenChange(false)
    setSearchQuery('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Search Foods</DialogTitle>
          <DialogDescription>Search USDA database or custom foods to add to your meal</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search for foods..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Tabs for USDA vs Custom */}
          <Tabs defaultValue="all" onValueChange={(v) => setSource(v === 'all' ? undefined : (v as 'usda' | 'custom'))}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">All Foods</TabsTrigger>
              <TabsTrigger value="usda">USDA Database</TabsTrigger>
              <TabsTrigger value="custom">Custom Foods</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Results */}
          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-[200px]" />
                      <Skeleton className="h-3 w-[150px]" />
                    </div>
                    <Skeleton className="h-8 w-16" />
                  </div>
                ))}
              </div>
            ) : foods.length > 0 ? (
              <div className="space-y-2">
                {foods.map((food) => (
                  <div
                    key={food._id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => handleSelectFood(food)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{food.name}</h4>
                        <Badge variant={food.source === 'usda' ? 'default' : food.source === 'ai' ? 'outline' : 'secondary'}>
                          {food.source === 'usda' ? 'USDA' : food.source === 'ai' ? 'AI' : 'Custom'}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {food.servingSize} {food.servingUnit} • {food.calories} cal • P: {food.macros.protein}g • C:{' '}
                        {food.macros.carbs}g • F: {food.macros.fat}g
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            ) : debouncedQuery.length >= 2 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No foods found matching "{debouncedQuery}"</p>
                {onCreateCustomFood && (
                  <Button variant="outline" className="mt-4" onClick={onCreateCustomFood}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Custom Food
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>Start typing to search for foods</p>
              </div>
            )}
          </ScrollArea>

          {/* Quick Action */}
          {onCreateCustomFood && (
            <div className="border-t pt-4">
              <Button variant="outline" className="w-full" onClick={onCreateCustomFood}>
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Food
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
